import { findIndex } from 'lodash';

import type { Money, SimpleChangeValue, SingleValueChange } from '../../../../apis/graphql';
import type { MultiValueChange } from '../../ChangeLogTable/ChangeLogTable';
import { LEGACY_UNIT_COST_CURRENCY, UNIT_COST_DTO_FIELD_NAME, UNIT_COST_FIELD_NAME } from '../../constants';
import { isMoney } from '../../utils/changesUtils';

const isPartialMoney = (payload: unknown): payload is Partial<Money> => typeof payload === 'object' && payload !== null;

const completeUnitCostDtoPayload = (payload: unknown, unitCost?: Money | null): Money | null => {
  if (isMoney(payload)) return payload;
  if (!isPartialMoney(payload)) return null;

  const currency = typeof payload.currency === 'string' ? payload.currency : unitCost?.currency;
  const micros = typeof payload.micros === 'number' ? payload.micros : unitCost?.micros;

  return typeof currency === 'string' && typeof micros === 'number' ? { currency, micros } : null;
};

const toCompletedUnitCostValue = (value: SimpleChangeValue, unitCost?: Money | null): SimpleChangeValue => ({
  ...value,
  payload: completeUnitCostDtoPayload(value.payload, unitCost),
});

const toUnitCostValue = (value: SimpleChangeValue): SimpleChangeValue => ({
  ...value,
  payload: typeof value.payload === 'number' ? { currency: LEGACY_UNIT_COST_CURRENCY, micros: value.payload } : null,
});

// No dto row at all: just USD-default the legacy row's raw number, no unification needed.
const applyLegacyOnlyUnitCost = (
  restructuredChangeList: Array<SingleValueChange | MultiValueChange>,
  unitCostIndex: number
): void => {
  if (unitCostIndex === -1) return;

  const legacyChange = restructuredChangeList[unitCostIndex] as SingleValueChange;
  const oldValue = toUnitCostValue(legacyChange.oldValue);
  const newValue = toUnitCostValue(legacyChange.newValue);

  // Leave the row untouched if neither side had a real number payload.
  if (oldValue.payload === null && newValue.payload === null) return;

  restructuredChangeList.splice(unitCostIndex, 1, { ...legacyChange, oldValue, newValue });
};

// Dto row present: fold it (plus any legacy row) into a single unit-cost row, then drop the dto row.
const applyUnifiedUnitCost = (
  restructuredChangeList: Array<SingleValueChange | MultiValueChange>,
  dtoIndex: number,
  unitCostIndex: number,
  previousUnitCostDto?: Money | null,
  currentUnitCostDto?: Money | null
): void => {
  const dtoChange = restructuredChangeList[dtoIndex] as SingleValueChange;
  const oldValue = toCompletedUnitCostValue(dtoChange.oldValue, previousUnitCostDto);
  const newValue = toCompletedUnitCostValue(dtoChange.newValue, currentUnitCostDto);

  if (oldValue.payload !== null || newValue.payload !== null) {
    const unifiedChange: SingleValueChange = {
      __typename: dtoChange.__typename,
      fieldName: UNIT_COST_FIELD_NAME,
      oldValue,
      newValue,
    };

    // Replace the legacy row in place when one exists, so the unified row keeps its position.
    if (unitCostIndex === -1) {
      restructuredChangeList.push(unifiedChange);
    } else {
      restructuredChangeList.splice(unitCostIndex, 1, unifiedChange);
    }
  } else if (unitCostIndex !== -1) {
    restructuredChangeList.splice(unitCostIndex, 1);
  }

  // Re-find the dto row: the splice/push above may have shifted its index, so `dtoIndex` is stale.
  const dtoIndexToRemove = findIndex(restructuredChangeList, { fieldName: UNIT_COST_DTO_FIELD_NAME });

  if (dtoIndexToRemove !== -1) {
    restructuredChangeList.splice(dtoIndexToRemove, 1);
  }
};

/**
 * Unifies `unit-cost` and `unit-cost-dto` into a single `unit-cost` row for display — always
 * exactly one formatted Money row, never a dropped row, never dedicated raw sub-field rows.
 *
 * Must run as a dedicated pass AFTER the main restructure loop — it removes an entry, which
 * would shift indices for any positional reads inside that loop. Locates entries with
 * `findIndex`, never a captured loop index.
 */
export const modifyUnitCost = (
  restructuredChangeList: Array<SingleValueChange | MultiValueChange>,
  previousUnitCostDto?: Money | null,
  currentUnitCostDto?: Money | null
): void => {
  const dtoIndex = findIndex(restructuredChangeList, { fieldName: UNIT_COST_DTO_FIELD_NAME });
  const unitCostIndex = findIndex(restructuredChangeList, { fieldName: UNIT_COST_FIELD_NAME });

  if (dtoIndex === -1) {
    applyLegacyOnlyUnitCost(restructuredChangeList, unitCostIndex);
    return;
  }

  applyUnifiedUnitCost(restructuredChangeList, dtoIndex, unitCostIndex, previousUnitCostDto, currentUnitCostDto);
};
